import type { ReactNode } from "react";
import { Popover } from "@headlessui/react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Icon } from "../Icon";
import { useFilterContext } from "./FilterContext";

interface FilterChipProps {
  children: ReactNode;
  "data-testid"?: string;
  renderSelectedValue: () => ReactNode;
}

export function FilterChip({
  children,
  "data-testid": dataTestId,
  renderSelectedValue,
}: Readonly<FilterChipProps>) {
  const { setReferenceElement, open: isFocused, onApply } = useFilterContext();
  const valueNode = renderSelectedValue();
  const hasValue = Boolean(valueNode);

  return (
    <Popover.Button
      ref={setReferenceElement}
      className="focus-visible:ring-secondary rounded-[3xl] focus-visible:ring-1"
      as="li"
    >
      <button
        type="button"
        data-testid={dataTestId}
        className={classNames(
          "hover:bg-secondary bg-primary group relative flex h-8 items-center rounded-3xl border px-[18px] py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          isFocused ? "text-dark border-light" : "text-default",
          hasValue && "border-darkest",
        )}
      >
        <div className="flex gap-x-2">
          <div className="text-dark flex max-w-[150px] items-center truncate text-sm font-medium leading-5">
            {children}
          </div>
          {hasValue && (
            <div className="flex items-center gap-x-1">
              <div className="max-w-[253px] truncate">{valueNode}</div>
            </div>
          )}
        </div>
        {hasValue && (
          <button
            type="button"
            className="bg-primary absolute right-1 top-1/2 hidden size-6 -translate-y-1/2 items-center justify-center rounded-3xl shadow-sm transition-all hover:shadow-md hover:outline hover:outline-1 group-hover:flex"
            onClick={event => {
              event.stopPropagation();
              onApply(null);
            }}
          >
            <Icon
              type="14-close"
              useWithStrokeCurrentColor
              size={14}
              className="!w-3.5"
            />
          </button>
        )}
      </button>
    </Popover.Button>
  );
}
